import { Table, Form, Input, Popconfirm, Space, Button, Select, Checkbox, Tag } from "antd";
import { useEffect } from "react";
import {
  DeleteUser,
  GetUsers,
  UpdateUser,
} from "../../Repository/UserManagementRepository";
import { useState } from "react";
import {
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
  CheckOutlined,
  SearchOutlined
} from "@ant-design/icons";
import { InfoMessage, SuccessMessage } from "../../Utils/Utils";
import { useDispatch, useSelector } from "react-redux";
import { GetUsersDataAction } from "../../redux/UserManagementRedux/UserManagementAction";

// EditableCell component for inline editing
const EditableCell = ({ dataIndex, children, editing, roles, record, form, ...restProps }) => {
  const [selectedRoles, setSelectedRoles] = useState([]);
  useEffect(() => {
    if (editing) {
      setSelectedRoles(record[dataIndex]?.split(',') || []);
    }
  }, [editing, record, dataIndex]);
  const tagRender = (props) => {
    const { label, closable, onClose } = props;

    return (
      <Tag
        color="blue"
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3 }}
      >
        {label}
      </Tag>
    );
  };

  // Use flatMap to split roles and filter out unique values
  const uniqueRoles = roles && roles.length > 0
    ? roles.flatMap(role => role.split(',').map(role => role.trim())) // Flatten and split roles
    : [];

  const uniqueRolesSet = [...new Set(uniqueRoles)]; // Ensure unique values

  return (
    <td {...restProps}>
      {editing && dataIndex === "Role" ? (
        <Form.Item name={dataIndex} style={{ margin: 0 }}>
         <Select
  mode="multiple"
  style={{ width: "100%" }}
  value={selectedRoles}
  onChange={(value) => {
    // We allow normal updates via checkboxes, so no logic here
    // This handler is still required by antd but actual control is in checkbox onChange
  }}
  tagRender={tagRender}
  dropdownRender={() => {
    const isSuperUserSelected = selectedRoles.includes("Super User");

    return (
      <div>
        {uniqueRolesSet.map((role) => {
          const shouldDisable =
            isSuperUserSelected && role !== "Super User";

          return (
            <div key={role} style={{ padding: "5px 12px" }}>
              <Checkbox
                disabled={shouldDisable}
                checked={selectedRoles.includes(role)}
                onChange={(e) => {
                  let updatedRoles = [];

                  if (e.target.checked) {
                    if (role === "Super User") {
                      updatedRoles = ["Super User"];
                    } else {
                      updatedRoles = [
                        ...selectedRoles.filter(r => r !== "Super User"),
                        role,
                      ];
                    }
                  } else {
                    updatedRoles = selectedRoles.filter(r => r !== role);
                  }

                  setSelectedRoles(updatedRoles);
                  form.setFieldsValue({ Role: updatedRoles });
                }}
              >
                {role}
              </Checkbox>
            </div>
          );
        })}
      </div>
    );
  }}
  defaultValue={record[dataIndex]?.split(',') || []}
/>


        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};






const UserTable = () => {
  // const [UsersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [EditingRecord, setEditingRecord] = useState(null);
  const IsRecordInEditingState = (key) => key === EditingRecord;
  const pageSize = 8;
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('Email');
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [roles, setRoles] = useState([]);
  // Using Redux to get UsersData from state
  const UsersData = useSelector((state) => state.userManagementReducer.users);
  // Fetch users data from repository
  async function fetchData() {
    try {
      setLoading(true)
      await dispatch(GetUsersDataAction());
      const Data = await GetUsers();
      if (Data) {
        const roles = Data.map(user => user.Role);
        setRoles([...new Set(roles)]);
      }

    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  }
  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(
      selectedKeys && selectedKeys.length > 0 ? selectedKeys[0] : ""
    );
    setSearchedColumn(dataIndex);
  };
  const handleReset = async (clearFilters) => {
    clearFilters();
    setSearchText("");
    try {
      let Data = await GetAllClientCreds();
      setClientsData(Data);
    } catch (error) {
      console.error("Error resetting filters:", error);
    }
  };

  const getColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    filteredValue: [searchText],
    onFilter: (value, record) =>
      record[dataIndex] !== undefined &&
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
      || false,

  });



  // Columns configuration for the user table
  const UserTableColumns = [
    {
      title: "Email Address",
      dataIndex: "Email",
      // editable: true,
      width: "50%",
      sorter: (a, b) => a.Email.localeCompare(b.Email),
      sortDirections: ["descend", "ascend"],
      onFilter: (value, record) => record.Email.indexOf(value) === 0,
      ...getColumnSearchProps('Email', 'Search Email'),
    },

    {
      title: "Role",
      dataIndex: "Role",
      editable: true,
      width: "40%",
      render: (text, record) => {
        return IsRecordInEditingState(record.Rg_Id) ? (
          text
        ) : (
          <span>{text}</span>
        );
      },
      onCell: (record) => ({
        record,
        dataIndex: "Role",
        title: "Role",
        editing: IsRecordInEditingState(record.Rg_Id),
        roles: roles,
      }),
    },

    // Actions column for editing and deleting
    {
      title: "Actions",
      dataIndex: "Actions",
      width: "10%",
      // editable: false,
      render: (_, record) => {
        return IsRecordInEditingState(record.Rg_Id) ? (
          // Edit and save buttons when editing
          <div className="FlexingRow Action-Table-Elements">
            <Popconfirm
              title="Are you sure to save?"
              onConfirm={() => SaveUser()}
            >
              <CheckOutlined className="check-icon" />
            </Popconfirm>
            <CloseOutlined
              className="cancel-icon"
              onClick={() => CancelEditing()}
            />
          </div>
        ) : (
          // Edit and delete buttons when not editing
          <div className="FlexingRow Action-Table-Elements">
            <EditOutlined
              className="edit-icon"
              onClick={() => EditUser(record)}
            />
            <Popconfirm
              title="Are you sure to delete?"
              onConfirm={() => DeleteUserAction(record)}
            >
              <DeleteOutlined className="delete-icon" />
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  const MergedColumns = UserTableColumns.map((column) => {
    if (column.editable) {
      return {
        ...column,
        onCell: (record) => ({
          record,
          dataIndex: column.dataIndex,
          title: column.title,
          editing: IsRecordInEditingState(record.Rg_Id),
          roles: roles,
          form: form
        }),
      };
    }
    return column;
  });

  // Map and transform data for the table
  const data =
    UsersData && UsersData.length > 0
      ? UsersData.map((user) => ({
        Rg_Id: user.Rg_Id,
        Email: user.Email,
        Role: user.Role,
      }))
      : [];

  // Edit a user's information
  const EditUser = (record) => {
    form.setFieldsValue({ ...record, Role: record.Role?.split(',') || [] });
    setEditingRecord(record.Rg_Id);
  };
  // Delete a user
  const DeleteUserAction = async (record) => {
    const Response = await DeleteUser(record.Rg_Id);
    if (Response != null) {
      SuccessMessage(Response);
      fetchData();
    }
  };

  // Cancel editing
  const CancelEditing = () => {
    setEditingRecord(null);
  };

  // Save edited user information
  const SaveUser = async () => {
    const editedValues = form.getFieldsValue();
    const originalValues = data.find(user => user.Rg_Id === EditingRecord);

    // Join roles into a comma-separated string (if they are in an array)
    const newRole = Array.isArray(editedValues.Role)
      ? editedValues.Role.join(',')
      : editedValues.Role; // If already a string, use as is

    // Ensure the Email field is part of the payload
    const newEmail = editedValues.Email || originalValues.Email;

    // Check if anything has changed
    const isEdited =
      originalValues.Role !== newRole ||
      originalValues.Email !== newEmail;

    if (isEdited) {
      const payload = {
        ...editedValues,
        Email: newEmail,  // Ensure Email is included in the payload
        Role: newRole,  // Pass Role as a string (comma-separated)
        Rg_Id: EditingRecord,  // Ensure Rg_Id is included
      };

      try {
        // Sending the payload to the backend
        const Response = await UpdateUser(payload);
        if (Response != null) {
          setEditingRecord(null);
          SuccessMessage(Response);
          fetchData();
        }
      } catch (error) {
        console.error('Error saving user:', error);
        // Handle error (e.g., show error message to the user)
      }
    } else {
      InfoMessage("No changes made. Nothing to save.");
    }
  };


  return (
    <Form form={form} component={false}>
      <div className="headingadd-div">
        <div className="page-heading1"> User Management </div>
        <Table
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          bordered
          columns={MergedColumns}
          loading={loading}
          dataSource={data}
          scroll={{ y: 410 }}
          pagination={false}
          className="Users-Table"
        />
      </div>
    </Form>
  );
};

export default UserTable;
